import { getRequiredServiceOffering } from '../../../../utils/parseServiceOfferings';
import { ServiceIds, nonInstallServiceIds } from '../../../../utils/constants/services';
import { ServicesApiClient } from '@jbhi-fi/apis/src/services/ServicesApiClient';
import { getIsServicesEnabled } from '../../../../utils/getIsServicesEnabled';
import { serviceHelper } from '../../../../config/service-helper';
import { getSession, setSession, SessionStorageKeys } from '@jbhi-fi/utils/src/sessionStorage';
import { ShopifyStorefrontClient } from '../../../../api/shopify/ShopifyStorefrontClient';
import { JbCartItem } from '@jbhi-fi/apis/src/shopify/types/JbCart';

export const serviceTypeId = (() => {
  const isNz = window.Jbhifi.storeLocation === 'NZ';
  const isShowOnlyExtraCareEnabled = window.featureFlags?.isShowOnlyExtraCareEnabled;

  if (isNz) {
    return isShowOnlyExtraCareEnabled ? ServiceIds.ExtraCareNz : ServiceIds.AppleCareNz;
  }
  return isShowOnlyExtraCareEnabled ? ServiceIds.ExtraCare : ServiceIds.MobileExtras;
})();

export type LineItems = Array<{ Sku: number; DisplayPriceInc: number; ProductGroupId: number }>;

export const removePDPServiceSelection = () => {
  setSession(SessionStorageKeys.PdpServiceSelection, '{}');
};

export const setPDPServiceSelection = (type: string, sku: string, selected: boolean) => {
  const serviceSelections = JSON.parse(getSession(SessionStorageKeys.PdpServiceSelection) ?? 'null');
  setSession(
    SessionStorageKeys.PdpServiceSelection,
    JSON.stringify({
      ...serviceSelections,
      [type]: {
        ...serviceSelections[type],
        [sku]: selected,
      },
    })
  );
};

export const initPDPServiceSelections = () => {
  removePDPServiceSelection();
  removeSelectedServiceSuggestion();
};

export const getSelectedServiceSuggestion = () => {
  return JSON.parse(getSession(SessionStorageKeys.PdpSelectedServiceSuggestion) ?? 'null');
};

export const setSelectedServiceSuggestion = (suggestion: any) => {
  setSession(SessionStorageKeys.PdpSelectedServiceSuggestion, suggestion, true);
};

export const removeSelectedServiceSuggestion = () => {
  setSession(SessionStorageKeys.PdpSelectedServiceSuggestion, '{}');
};

export const isPdpServiceSelected = (type: string, sku: string) => {
  const serviceSelections = JSON.parse(getSession(SessionStorageKeys.PdpServiceSelection) ?? 'null');
  const selected = serviceSelections?.[type]?.[sku] ? true : false;
  return selected;
};

export const containsExtras = services => services?.some(service => (service.Id || service.id) === serviceTypeId);

const getTheExtraTypeOfServiceToOfferToTheCustomer = (productSku: string, productGroupId?: number): ServiceIds => {
  const isNz = window.Jbhifi.storeLocation === 'NZ';
  const isShowOnlyExtraCareEnabled = window.featureFlags?.isShowOnlyExtraCareEnabled;

  //1. if a feature flag is enabled for "JB Hi-Fi Exta Care service", then offer "JB Hi-Fi Exta Care service"
  if (isShowOnlyExtraCareEnabled) {
    return isNz ? ServiceIds.ExtraCareNz : ServiceIds.ExtraCare;
  }

  //2. if there are "override" (theme) settings applicable to product group or sku, then offer "extra care" service
  if (
    window.Jbhifi?.offerExtraCareService?.productSkus?.map((sku: string) => sku.trim()).includes(String(productSku)) ||
    window.Jbhifi?.offerExtraCareService?.productGroupIds
      ?.map((groupId: string) => groupId.trim())
      .includes(String(productGroupId))
  ) {
    return isNz ? ServiceIds.ExtraCareNz : ServiceIds.ExtraCare; //offer "JB Hi-Fi Extra Care" service
  }

  //default extras service
  return isNz ? ServiceIds.AppleCareNz : ServiceIds.MobileExtras;
};

const normId = (s: any): ServiceIds => (s.Id ?? s.id) as ServiceIds;

const getServicesToOffer = (services: any, productSku: string, productGroupId?: number): Array<ServiceIds> => {
  if (!services?.length) return [];

  const ids: Array<ServiceIds> = [];

  const installService = services.find(s => normId(s) === ServiceIds.Install);
  if (installService) {
    ids.push(normId(installService));
  }

  const nonInstallServices = services.filter(s => normId(s) !== ServiceIds.Install);

  if (nonInstallServices.length === 1) {
    ids.push(normId(nonInstallServices[0]));
  } else if (containsExtras(nonInstallServices)) {
    ids.push(getTheExtraTypeOfServiceToOfferToTheCustomer(productSku, productGroupId));
  }

  return Array.from(new Set(ids));
};

export const parseServiceSuggestions = (
  lineItemsWithSuggestions,
  productInfo?: Array<{ productSku: string; productGroupId?: number }>
) => {
  return lineItemsWithSuggestions?.map(lineItemWithSuggestion => {
    const servicesKey = lineItemWithSuggestion.Services ? 'Services' : 'services';
    const offeringsKey = lineItemWithSuggestion.Services ? 'Offerings' : 'offerings';
    const idKey = lineItemWithSuggestion.Services ? 'Id' : 'id';

    const relevantServices = lineItemWithSuggestion[servicesKey]
      ?.filter(service => Object.values(ServiceIds).includes(service[idKey]))
      .filter(service =>
        window.Jbhifi?.installConfig?.installServicesEnabled
          ? true
          : (service[idKey] ?? service.Id) !== ServiceIds.Install
      );

    const serviceIdsToOffer = getServicesToOffer(
      relevantServices,
      lineItemWithSuggestion.SKU || lineItemWithSuggestion.productSku,
      productInfo?.find(p => p.productSku === (lineItemWithSuggestion.SKU || lineItemWithSuggestion.productSku))
        ?.productGroupId //get product group id for associated product sku
    );

    const updatedServices = relevantServices.map(service => {
      return {
        ...service,
        [offeringsKey]: getRequiredServiceOffering(
          lineItemWithSuggestion.SKU || lineItemWithSuggestion.productSku,
          service.Id || service.id,
          service[offeringsKey]
        ),
        shouldRender: serviceIdsToOffer.includes(service[idKey]),
      };
    });
    return { ...lineItemWithSuggestion, [servicesKey]: updatedServices };
  });
};

export const fetchServiceSuggestions = async (lineItems: LineItems): Promise<Array<any> | undefined> => {
  const isServicesEnabled = getIsServicesEnabled();
  const servicesApiClient = new ServicesApiClient({
    serviceSuggestionsUrl: serviceHelper.serviceSuggestionsUrl,
    countryCode: window.Jbhifi.storeLocation,
  });

  if (!isServicesEnabled) return;

  const data = {
    sourceSystem: 'Shopify',
    suggestionMarkupFormat: 'Markdown',
    lineItems: lineItems.map(li => ({ productSku: li.Sku, finalProductPriceInc: li.DisplayPriceInc })),
  };
  const { data: response, error } = await servicesApiClient.getSuggestions(data);
  if (error) {
    console.error('Error while getting service suggestions: ', error);
    return;
  }

  const productInfo = lineItems.map(li => ({
    productSku: String(li.Sku),
    productGroupId: li.ProductGroupId,
  }));

  return response?.lineItems ? parseServiceSuggestions(response?.lineItems, productInfo) : [];
};

export const getLineItemsEligibleForServices = (lineItems: Array<JbCartItem>): Array<JbCartItem> => {
  if (!lineItems) return [];

  return lineItems.filter(
    item => !item.properties?._eligibleForServices || item.properties?._eligibleForServices === 'true'
  );
};

const storefrontClient = new ShopifyStorefrontClient();

export const getProductServiceVariantId = async (sku: string): Promise<number> => {
  const product = await storefrontClient.getProductByHandle(`service-${sku}`);

  const variant = product?.variants?.nodes?.[0];

  if (!variant) throw 'unable to find shopify variant for service';

  return Number(variant?.id.split('/').pop());
};

export const IsAttachedService = (item: JbCartItem) => {
  return item.properties?._isAttachedService?.toString() === 'true';
};
